import React from 'react';
import { ProgressIndicator } from '@veneer/core';
import { useTheme } from '@veneer/theme';

import { CategoryType, Device } from '../../types/devices';
import { OperationStatus } from '../../types';
import ApiError from '../../types/error';

import { useDependencyManagerContext } from '../../contexts/dependencyManager';

import { ErrorDisplay } from '../common';
import DeviceItem from '../device-item';

import { DeviceCardContainer } from '../device-item/styled';

type DeviceListProps = {
  status: OperationStatus | null;
  type: CategoryType;
  devices: Device[];
  error: ApiError;
  retryAction?: () => void;
};

export const NoDevices: React.FC<{ type: CategoryType }> = ({ type }) => {
  const { translate } = useDependencyManagerContext();
  const theme = useTheme();

  return (
    <p
      data-testid={`react-device-management-mfe-category-${type}-no-device-found`}
      style={{ color: theme.color.foreground.light }}
    >
      {type === CategoryType.PERSONAL_SYSTEMS
        ? translate('components.device-category.no-data-requested')
        : translate('components.device-category.no-device-found')}
    </p>
  );
};

const DeviceList: React.FC<DeviceListProps> = ({
  status,
  type,
  devices,
  error,
  retryAction
}) => {
  const theme = useTheme();

  if (status === OperationStatus.RUNNING) {
    return <ProgressIndicator />;
  }
  if (error && error?.statusCode === 410) {
    if (retryAction) retryAction();
    return null;
  }
  if (status === OperationStatus.ERRORED) {
    return (
      <ErrorDisplay
        dataTestId={`react-device-management-mfe-category-${type}-error-display`}
        error={error}
        retryAction={retryAction}
      />
    );
  }
  if (status === OperationStatus.COMPLETED && devices.length > 0) {
    return (
      <DeviceCardContainer
        data-testid={`react-device-management-mfe-category-${type}-devices`}
        $veneerTheme={theme}
      >
        {devices.map((device: Device, index: number) => {
          return (
            <DeviceItem
              data-testid={`react-device-management-mfe-category-${type}-device-${device.device_serial_nr}`}
              key={device.device_serial_nr}
              device={device}
              CategoryType={type}
            />
          );
        })}
      </DeviceCardContainer>
    );
  }
  return <NoDevices type={type} />;
};

export default DeviceList;
